import { Radio } from '@material-tailwind/react';
import classNames from 'classnames';
import { Prose } from 'components/modulab/Prose';
import { createValidityState } from 'hooks/forms';
import { ComponentProps, JSX, useEffect, useRef, useState } from 'react';

type Props = Omit<ComponentProps<typeof Radio>, 'error'> & {
  className?: string;
  containerClassname?: string;
  onValidate?: (validity: { valid: boolean }) => void | string;
  radioContainerClassname?: string;
  required?: boolean;
  options: {
    value: string;
    label: string | JSX.Element;
  }[];
};

export function AlphaRadio(props: Props): JSX.Element {
  const {
    className,
    containerClassname,
    onValidate,
    radioContainerClassname,
    required = false,
    options,
    ...passThrough
  } = props;

  type Value = string | number | readonly string[] | undefined;
  const [state, setState] = useState<Value>(
    () => props.value ?? props.defaultValue,
  );
  const [interaction, setInteraction] = useState<boolean>(false);
  const inputRef = useRef<HTMLInputElement | null>(null);

  const validity = createValidityState({
    valueMissing: Boolean(required && !state),
  });

  const errorMessage = onValidate?.(validity) ?? '';

  useEffect(() => {
    inputRef.current?.setCustomValidity(errorMessage);
  }, [errorMessage]);

  return (
    <>
      <input
        type="text"
        hidden
        required
        name={props.name}
        value={state ?? ''}
        onInvalid={() => setInteraction(true)}
        // A change event handler is required to suppress a React warning, but not strictly required
        onChange={(event) => setState(event.currentTarget.value)}
      />
      <div className={classNames(className ? className : 'flex flex-col')}>
        {options.map((option) => (
          <label
            className={classNames(
              'flex w-full cursor-pointer items-center',
              containerClassname,
            )}
            htmlFor={`${props.name}-${option.value}`}
            key={option.value}
          >
            <Radio
              {...passThrough}
              defaultChecked={option.value === state}
              id={`${props.name}-${option.value}`}
              name={props.name}
              ripple={false}
              className="transition-all duration-200 before:hidden checked:border-primary-800 checked:bg-primary-800 checked:before:bg-primary-800 hover:!border-border/form-hover hover:shadow-md lg:size-6"
              iconProps={{
                className: 'text-white [&>svg]:w-[10px] [&>svg]:h-[10px]',
              }}
              containerProps={{
                className: classNames('m-1 mr-2 p-0', radioContainerClassname),
              }}
              onChange={(e) => {
                if (state === e.target.value) return;
                setState(e.target.value);
                setInteraction(true);
                props.onChange?.(e);
              }}
              value={option.value}
            />
            <p className="text-text/primary alpha-body-2">
              {typeof option.label === 'string' ? (
                <Prose
                  dangerouslySetInnerHTML={{
                    __html: option.label,
                  }}
                />
              ) : (
                option.label
              )}
            </p>
          </label>
        ))}
      </div>
      {interaction && errorMessage && (
        <div className="px-padding/x-sm pt-1 text-highlights/red-dark alpha-caption">
          {errorMessage}
        </div>
      )}
    </>
  );
}
